import {
  Box,
  Button,
  Flex,
  Text,
  HStack,
  Heading,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import Page from "@ui/components/Page";
import { Download } from "lucide-react";
import { useSelf } from "@ui/services/useUser";
import { useTodaySupplierMeals } from "@/services/useSupplierPortal";
import { MealDictionary } from "../MealBooking/MealOption";
import { DateTime } from "luxon";
import { motion } from "framer-motion";
import UserProfile from "@/components/UserProfile";
import { withModalForm } from "@ui/components/forms";
import HistoricalForm from "./HistoricalReport";

const Historical = withModalForm(HistoricalForm);

const MotionBox = motion(Box);

export default function SupplierPortal() {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { data: me } = useSelf<any>();
  const { data: todayMealsData, isLoading } = useTodaySupplierMeals(me?.id);

  const date = DateTime.now().toFormat("dd MMM yyyy");
  const allMeals = ["breakfast", "lunch", "dinner"];
  const mealsArray = Array.isArray(todayMealsData)
    ? todayMealsData
    : (todayMealsData?.meals ?? []);

  return (
    <Page
      title={
        <Flex direction="row" w={"100%"} alignItems="center" gap={2} px={6}>
          <HStack w={"50%"}>
            <UserProfile variant="supplier-portal" />
          </HStack>
          <HStack alignItems={"center"} w={"50%"}>
            <Heading variant="H700">
              {todayMealsData?.[0]?.supplier?.name ??
                todayMealsData?.supplierName ??
                "Supplier"}
            </Heading>
            <Heading variant="H500">{`(`}</Heading>
            <Heading variant="H500">{date}</Heading>
            <Heading variant="H500">{`)`}</Heading>
          </HStack>
        </Flex>
      }
      actions={
        <Box>
          <Button
            leftIcon={<Download size={"18px"} />}
            colorScheme="blue"
            onClick={onOpen}
          >
            Download Report
          </Button>
        </Box>
      }
    >
      <Box h={"full"}>
        {isLoading ? (
          <Box mt={6} textAlign="center">
            <Spinner size="lg" color="green.500" thickness="4px" />
            <Text mt={2} fontSize="sm" color="gray.500">
              Loading bookings data...
            </Text>
          </Box>
        ) : (
          <Box
            h={"full"}
            display={"flex"}
            alignItems={"center"}
            alignContent={"center"}
            justifyContent={"center"}
          >
            <HStack
              spacing={6}
              align="stretch"
              flexWrap="wrap"
              justifyContent={"center"}
            >
              {allMeals.map((mealType) => {
                const mealData = mealsArray.find(
                  (m: any) => m.meal === mealType,
                );
                const Icon =
                  MealDictionary[mealType as keyof typeof MealDictionary]?.icon;
                const label =
                  MealDictionary[mealType as keyof typeof MealDictionary]
                    ?.label;

                const count = mealData?.count ?? 0;
                const hasData = !!mealData;

                const bgGradient = hasData
                  ? "linear(to-br, green.200, green.100)"
                  : "linear(to-br, orange.200, orange.100)";
                const borderColor = hasData ? "green.400" : "orange.400";
                const textColor = hasData ? "green.700" : "orange.700";

                return (
                  <MotionBox
                    key={mealType}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <MotionBox
                      px={6}
                      py={10}
                      shadow="xl"
                      minW="250px"
                      w={"400px"}
                      maxW="400px"
                      maxH={"450px"}
                      h="auto"
                      bgGradient={bgGradient}
                      borderColor={borderColor}
                      borderRadius="2xl"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      cursor="default"
                      color={textColor}
                    >
                      <Flex
                        justifyContent={"center"}
                        direction={"column"}
                        align="center"
                        gap={6}
                        maxW={"500px"}
                        h={"full"}
                      >
                        <Box>
                          <Icon
                            size={50}
                            stroke={hasData ? "green" : "orange"}
                          />
                        </Box>
                        <Box>
                          <Text
                            fontSize="2xl"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            {label}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="4xl" fontWeight="bold">
                            {count}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold">
                            {!todayMealsData?.[mealType]
                              ? "Unavailable"
                              : hasData
                                ? "Available"
                                : "Data not available before cut-off"}
                          </Text>
                        </Box>
                        {mealData?.fields?.locationCount?.length > 0 ? (
                          <Box>
                            <Flex
                              direction="column"
                              align="center"
                              gap={1}
                              maxH="150px"
                              overflowY="auto"
                            >
                              {mealData.fields.locationCount.map((loc: any) => (
                                <HStack key={loc.masterDataId}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {loc.location}:
                                  </Text>
                                  <Text fontSize="sm" fontWeight="semibold">
                                    {loc.count}
                                  </Text>
                                </HStack>
                              ))}
                            </Flex>
                          </Box>
                        ) : (
                          <Text
                            fontSize="xs"
                            mt={2}
                            color="gray.500"
                            textAlign="center"
                          >
                            Location data unavailable
                          </Text>
                        )}
                      </Flex>
                    </MotionBox>
                  </MotionBox>
                );
              })}
            </HStack>
          </Box>
        )}
        {isOpen && <Historical isOpen={isOpen} onClose={onClose} />}
      </Box>
    </Page>
  );
}
